import { useRef, useState } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { IMAGE_SLOTS, uploadImageAsset, validateImageFile } from '../utils/imageUpload.js';
import { userSession } from '../utils/userSession.js';

export default function ImageUrlField({
  id,
  name,
  label,
  value,
  onChange,
  type,
  entityId,
  pendingFile,
  onPendingFile
}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const slot = IMAGE_SLOTS[type];

  const handlePick = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file || !slot) return;

    setError('');
    setBusy(true);
    try {
      await validateImageFile(file, type);
      if (entityId) {
        const url = await uploadImageAsset({
          type,
          entityId,
          file,
          sessionToken: userSession.getUser()?.sessionToken
        });
        onChange(url);
        if (onPendingFile) onPendingFile(null);
      } else if (onPendingFile) {
        onPendingFile(file);
      } else {
        throw new Error('Save the record before uploading.');
      }
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <div className="mt-1 flex items-stretch gap-2">
        <input
          ref={fileRef}
          type="file"
          accept={slot?.accept}
          className="hidden"
          onChange={handlePick}
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => fileRef.current && fileRef.current.click()}
          title={slot?.hint || 'Upload image'}
          className="inline-flex items-center justify-center px-2.5 rounded-md border border-gray-300 bg-white text-slate-700 hover:bg-slate-50 disabled:text-slate-400"
        >
          <PhotoIcon className="h-5 w-5" />
          <span className="sr-only">Upload image</span>
        </button>
        <input
          type="url"
          id={id}
          name={name}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
        />
      </div>
      <p className="mt-1 text-xs text-gray-500">
        {busy
          ? 'Uploading…'
          : pendingFile
            ? `Ready to upload on save: ${pendingFile.name}`
            : slot?.hint}
      </p>
      {error && (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      )}
    </div>
  );
}
